import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { Observable } from '@apollo/client/utilities'
import { toast } from 'react-toastify'
import { GRAPHQL_HOST, THEME } from 'src/config/config'
import { ensureValidIdToken } from 'src/hooks/tokenRefreshGuard'
import { logoutAndRedirect } from 'src/utils/authLogout'

const isSSOUser = () => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('sso') === 'true'
  } catch (error) {
    return false
  }
}

const showSessionExpiredToast = () => {
  const toastId = 'session-expired-toast'
  if (!toast.isActive(toastId)) {
    toast.warning('Your session expired. Please sign in again.', {
      toastId,
      autoClose: 4000,
      theme: 'colored',
      onClose: async () => {
        // Use centralized logout function (event listener in _app.tsx will handle Redux)
        await logoutAndRedirect('Session expired - toast closed', '/signout')
      },
    })
  }
}

let apolloClientInstance: ApolloClient<NormalizedCacheObject>

// Logging link for successful requests
const loggingLink = setContext(async (operation, { headers }) => {
  if (process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG === 'true') {
    const startTime = Date.now()
    const operationName = operation.operationName
    const requestId = Math.random().toString(36).substring(7)

    // Log request start
    const timestamp = new Date().toISOString().replace('T', ' ').replace('Z', '')
    // eslint-disable-next-line no-console
    if (typeof window === 'undefined') {
      // eslint-disable-next-line no-console
      console.info(` apollo client [${timestamp}] http: POST ${GRAPHQL_HOST} ${operationName} START`)
    }
    return {
      headers: {
        ...headers,
        'x-request-start-time': startTime.toString(),
        'x-request-id': requestId,
      },
    }
  }

  return {
    headers: {
      ...headers,
    },
  }
})

const authLink = setContext(async (_, { headers }) => {
  const freshIdToken = await ensureValidIdToken()
  return {
    headers: {
      ...headers,
      ...(freshIdToken ? { Authorization: `Bearer ${freshIdToken}` } : {}),
    },
  }
})

// Enhanced error link with logging
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isAuthError =
    (networkError && 'statusCode' in networkError && networkError.statusCode === 401) ||
    graphQLErrors?.some(
      (err) =>
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message?.toLowerCase().includes('unauthorized') ||
        err.message?.toLowerCase().includes('authentication'),
    )

  /* ---------------------------------------
   * Existing logging (unchanged)
   * ------------------------------------- */
  if (process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG === 'true') {
    const startTime = operation.getContext()['x-request-start-time']
    const requestId = operation.getContext()['x-request-id']
    const duration = startTime ? Date.now() - parseInt(startTime) : 0
    const timestamp = new Date().toISOString().replace('T', ' ').replace('Z', '')

    if (networkError) {
      const statusCode = 'statusCode' in networkError ? networkError.statusCode : 'NETWORK_ERROR'
      // eslint-disable-next-line no-console
      console.error(
        `[${timestamp}] http: POST ${GRAPHQL_HOST} ${operation.operationName} FAILED (${duration} ms) ${statusCode}`,
        {
          'Error Type': 'Network Error',
          'Status Code': statusCode,
          Message: networkError.message || 'N/A',
          'Request ID': requestId || 'N/A',
        },
      )
    }

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // eslint-disable-next-line no-console
        console.error(
          `[${timestamp}] http: POST ${GRAPHQL_HOST} ${operation.operationName} FAILED (${duration} ms) ${
            err.extensions?.code || 'GRAPHQL_ERROR'
          }`,
          {
            'Error Type': 'GraphQL Error',
            'Error Code': err.extensions?.code || 'N/A',
            Message: err.message || 'N/A',
            'Request ID': requestId || 'N/A',
          },
        )
      }
    }
  }

  /* ---------------------------------------
   * Not an auth error → do nothing
   * ------------------------------------- */
  if (!isAuthError) {
    return
  }

  const { _authRetry, _authRetryCount = 0 } = operation.getContext()
  const MAX_RETRY_ATTEMPTS = 2

  // ✅ Prevent infinite retry loops
  if (_authRetryCount >= MAX_RETRY_ATTEMPTS) {
    // eslint-disable-next-line no-console
    console.error('Max retry attempts reached for auth error. Logging out.')

    if (!isSSOUser()) {
      showSessionExpiredToast()
    } else {
      // Use centralized logout function (event listener in _app.tsx will handle Redux)
      logoutAndRedirect('Max retry attempts reached', '/signout')
    }
    return
  }

  if (_authRetry) {
    return
  }

  // ✅ MARK OPERATION AS RETRIED and increment counter
  operation.setContext({ _authRetry: true, _authRetryCount: _authRetryCount + 1 })

  /* ---------------------------------------
   * Auth error → refresh token & retry
   * ------------------------------------- */
  return new Observable((observer) => {
    ensureValidIdToken() // force refresh, guarded internally
      .then((refreshedToken) => {
        // ✅ Check if refresh actually succeeded (returned a valid token)
        if (!refreshedToken) {
          // Refresh failed (e.g., refresh token expired) → logout
          if (!isSSOUser()) {
            // Non-SSO: toast handles sign-out + redirect
            showSessionExpiredToast()
          } else {
            // SSO: use centralized logout function (event listener in _app.tsx will handle Redux)
            logoutAndRedirect('Token refresh failed', '/signout')
          }

          observer.error(new Error('Session expired - unable to refresh token'))
          return
        }

        // ✅ We have a valid token, retry the operation
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        })
      })
      .catch(() => {
        // 🔴 Refresh threw an error → logout
        if (!isSSOUser()) {
          // Non-SSO: toast handles sign-out + redirect
          showSessionExpiredToast()
        } else {
          // SSO: use centralized logout function (event listener in _app.tsx will handle Redux)
          logoutAndRedirect('Token refresh error', '/signout')
        }

        observer.error(new Error('Session expired'))
      })
  })
})

// Apollo Link to add fingerprint, IP address, and request ID headers
const enhancedHeadersLink = setContext(async (operation, { headers }) => {
  const enhancedHeaders = {
    ...headers,
    'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth',
  }

  try {
    // Get fingerprint
    const { getFingerprint } = await import('src/components/CustomHooks/useFingerprint')
    const fingerprint = await getFingerprint()
    if (fingerprint) {
      enhancedHeaders['X-Fingerprint-ID'] = fingerprint
    }

    // Get IP address
    const { getIPAddress } = await import('src/components/CustomHooks/useIPAddress')
    const ipAddress = await getIPAddress()
    if (ipAddress) {
      enhancedHeaders['X-IP-Address'] = ipAddress
    }

    // Get encoded unique ID
    const { getEncodedUniqueId } = await import('src/components/CustomHooks/useUniqueId')
    const encodedUniqueId = await getEncodedUniqueId()
    if (encodedUniqueId) {
      enhancedHeaders['X-Request-ID'] = encodedUniqueId
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error adding enhanced headers:', error)
  }

  return {
    headers: enhancedHeaders,
  }
})

const httpLink = new HttpLink({
  uri: GRAPHQL_HOST,
  headers: {
    'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth',
  },
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// TODO(auth): setAuthToLink is now a no-op because authLink injects/refreshed the latest idToken per request.
// TODO(auth): Remove all call sites of setAuthToLink(...) across the app once verified in production
// (e.g., _app.tsx, signin, vendor-signin, sso/callback, and any other login/refresh flows).

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const setAuthToLink = (_token: string) => {
  // No-op: authLink injects the latest refreshed idToken per request.
}

export const useApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  if (!apolloClientInstance) {
    apolloClientInstance = new ApolloClient({
      cache: new InMemoryCache({
        typePolicies: {
          ContentEntity: {
            keyFields: (object, context) => {
              if (context.fragmentMap?.vendorAnalysisForSolution) {
                return ['attributes', ['slug']]
              }
              return ['id']
            },
          },
        },
      }),
      link: from([
        errorLink,
        loggingLink,
        authLink,
        ...(process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG == 'true' ? [enhancedHeadersLink] : []),
        httpLink,
      ]),
      connectToDevTools: process.env.NODE_ENV === 'development',
    })
  }

  return apolloClientInstance
}

export const resetApolloClient = () => {
  apolloClientInstance = new ApolloClient({
    cache: new InMemoryCache(), // Reset cache
    link: from([
      errorLink,
      loggingLink,
      authLink,
      ...(process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG == 'true' ? [enhancedHeadersLink] : []),
      new HttpLink({
        uri: GRAPHQL_HOST,
      }),
    ]),
    connectToDevTools: process.env.NODE_ENV === 'development',
  })
}
